import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Calendar, Sparkles, ArrowRight, Star, Quote,
  Scissors, Package, MapPin, Clock, Globe, ParkingCircle, Users, Phone,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useLang } from '../context/LanguageContext';
import { HERO_IMAGE_URL } from '../constants/brand';
import api from '../utils/api';

const TESTIMONIALS = [
  { name: 'Sophie D.', text: 'Absolutely the best salon experience in Diegem. Nidhi and her team made me feel like royalty — my hair has never looked better!', rating: 5 },
  { name: 'Amélie L.', text: 'Warm, welcoming and incredibly professional. The attention to detail is unmatched. I always leave feeling beautiful and relaxed.', rating: 5 },
  { name: 'Priya S.', text: 'A true luxury studio with a personal touch. They speak my language, listen to what I want, and deliver every single time.', rating: 5 },
];

const Home = () => {
  const { localize } = useLang();
  const [offers, setOffers] = useState([]);
  const [services, setServices] = useState([]);

  useEffect(() => {
    api.get('/promotions').then(({ data }) => setOffers(data.slice(0, 3))).catch(() => {});
    api.get('/services').then(({ data }) => {
      const featured = data.filter((s) => s.featured);
      const list = (featured.length >= 6 ? featured : data).slice(0, 6);
      setServices(list);
    }).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-[#faf6ec]">
      {/* HERO — split: dark green text panel + single full image (no cropping duplication) */}
      <section data-testid="home-hero" className="grid grid-cols-1 lg:grid-cols-2 min-h-[85vh] bg-[#04140e]">
        {/* Text panel */}
        <div className="order-2 lg:order-1 flex items-center px-6 sm:px-10 lg:px-16 py-14 lg:py-0">
          <div className="max-w-xl">
            <div className="inline-flex items-center space-x-3 mb-5">
              <span className="h-px w-10 bg-[#c5a059]"></span>
              <span className="text-[#c5a059] text-[11px] tracking-[0.3em]">LUXURY SALON EXPERIENCE</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-bold leading-[0.95] mb-1 text-[#f5e9ce]">
              BEAUTY &amp; STYLE
            </h1>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-bold italic leading-[0.95] text-[#c5a059] mb-6">
              REDEFINED
            </h1>
            <p className="text-[#f5e9ce]/90 text-base sm:text-lg mb-1">Where Elegance Meets Expertise</p>
            <p className="text-[#f5e9ce]/60 mb-8">Reveal Your True Beauty</p>
            <div className="flex flex-wrap gap-3">
              <Link to="/services">
                <Button size="lg" className="bg-[#c5a059] text-[#04140e] hover:bg-[#d4b56e] font-semibold px-7 py-5 rounded-full" data-testid="home-cta-book">
                  <Calendar className="w-5 h-5 mr-2" /> Book Your Appointment
                </Button>
              </Link>
              <Link to="/about" className="hidden sm:inline-block">
                <Button size="lg" variant="outline" className="border-[#c5a059] text-[#c5a059] hover:bg-[#c5a059] hover:text-[#04140e] px-7 py-5 rounded-full bg-transparent" data-testid="home-cta-discover">
                  Discover Our Salon
                </Button>
              </Link>
            </div>
          </div>
        </div>
        {/* Single hero image */}
        <div className="order-1 lg:order-2 relative min-h-[340px] sm:min-h-[440px] lg:min-h-full">
          <img
            src={HERO_IMAGE_URL}
            alt="4AN luxury salon experience"
            className="absolute inset-0 w-full h-full object-cover object-center"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-t lg:bg-gradient-to-r from-[#04140e] via-[#04140e]/20 to-transparent lg:via-transparent"></div>
        </div>
      </section>

      {/* Highlights bar */}
      <section className="bg-[#04140e] border-t border-[#c5a059]/15">
        <div className="max-w-7xl mx-auto px-6 py-5 grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="home-highlights">
          {[
            { icon: Users, title: 'EXPERT STYLISTS', desc: 'Trained Professionals' },
            { icon: Package, title: 'PREMIUM PRODUCTS', desc: 'Top Quality Brands' },
            { icon: Sparkles, title: 'LUXURY EXPERIENCE', desc: 'For Him & Her' },
            { icon: Scissors, title: 'UNISEX SALON', desc: 'Everyone is Welcome' },
          ].map((h, i) => (
            <div key={i} className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-full border border-[#c5a059] flex items-center justify-center text-[#c5a059] flex-shrink-0">
                <h.icon className="w-4 h-4" />
              </div>
              <div className="leading-tight">
                <p className="text-[#c5a059] text-[10px] sm:text-[11px] tracking-[0.15em] font-semibold">{h.title}</p>
                <p className="text-[#f5e9ce]/60 text-[10px] sm:text-xs">{h.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Quick info bar */}
      <section className="bg-[#faf6ec] border-t border-[#c5a059]/15">
        <div className="max-w-7xl mx-auto px-6 py-6 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm" data-testid="home-quickinfo">
          {[
            { icon: Clock, title: 'OPENING HOURS', lines: ['10:00 – 19:00', 'Monday Closed'] },
            { icon: ParkingCircle, title: 'FREE PARKING', lines: ['Available'] },
            { icon: Globe, title: 'WE SPEAK', lines: ['Dutch, English, French'] },
            { icon: MapPin, title: 'LOCATION', lines: ['Alfons de Cockplein 5A', '1831 Diegem'] },
          ].map((q, i) => (
            <div key={i} className="flex items-start gap-2.5">
              <q.icon className="w-4 h-4 text-[#c5a059] flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-[#c5a059] text-[9px] sm:text-[10px] tracking-widest">{q.title}</p>
                {q.lines.map((l, j) => (
                  <p key={j} className={`${j === 0 ? 'text-[#0d3b25]/80' : 'text-[#0d3b25]/50'} text-[11px] sm:text-xs leading-tight`}>{l}</p>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Services preview */}
      {services.length > 0 && (
        <section className="py-16 bg-[#faf6ec] border-t border-[#c5a059]/15" data-testid="home-services">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-10">
              <span className="text-[#c5a059] text-[11px] tracking-[0.3em]">— WHAT WE OFFER</span>
              <h2 className="text-3xl md:text-4xl font-serif font-bold text-[#0d3b25] mt-3 mb-2">Signature Services</h2>
              <p className="text-[#0d3b25]/70 text-sm max-w-2xl mx-auto">Hair, colour, nails, skin & more — crafted with premium products and expert care.</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {services.map((s) => (
                <Link
                  key={s.id}
                  to={`/book?service=${s.id}`}
                  data-testid={`home-service-${s.id}`}
                  className="bg-white border border-[#c5a059]/30 rounded-sm overflow-hidden hover:shadow-2xl hover:border-[#c5a059] transition-all group flex flex-col"
                >
                  <div className="relative aspect-[4/3] bg-[#04140e]/60 overflow-hidden">
                    {s.image_url ? (
                      <img src={s.image_url} alt={localize(s, 'name')} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-[#c5a059]/50"><Sparkles className="w-12 h-12" /></div>
                    )}
                    {s.featured && <span className="absolute top-2 left-2 bg-[#c5a059] text-[#0d3b25] text-[10px] px-2 py-0.5 rounded-sm font-bold uppercase tracking-wider">Best Seller</span>}
                  </div>
                  <div className="p-5 flex flex-col flex-grow">
                    <h3 className="font-serif font-bold text-lg text-[#0d3b25] line-clamp-1">{localize(s, 'name')}</h3>
                    <div className="flex items-center text-xs text-[#0d3b25]/50 mt-1 mb-3"><Clock className="w-3 h-3 mr-1" /> {s.duration} min</div>
                    <div className="flex items-center justify-between pt-3 border-t border-[#c5a059]/20 mt-auto">
                      <span className="font-serif font-bold text-xl text-[#c5a059]">€{s.price.toFixed(2)}</span>
                      <span className="text-xs font-semibold text-[#0d3b25] inline-flex items-center gap-1 group-hover:text-[#c5a059] transition-colors">Book <ArrowRight className="w-3 h-3" /></span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
            <div className="text-center mt-10">
              <Link to="/services">
                <Button size="lg" variant="outline" className="border-[#c5a059] text-[#0d3b25] hover:bg-[#c5a059] hover:text-[#04140e] px-10 py-5 rounded-full font-semibold">
                  View All Services <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Featured Bingo Offers */}
      {offers.length > 0 && (
        <section className="py-16 bg-[#04140e] border-t border-[#c5a059]/15" data-testid="home-offers">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-10">
              <span className="text-[#c5a059] text-[11px] tracking-[0.3em]">— EXCLUSIVE PACKAGES</span>
              <h2 className="text-3xl md:text-4xl font-serif font-bold text-[#f5e9ce] mt-3 mb-2">Bingo Beauty Deals</h2>
              <p className="text-[#f5e9ce]/70 text-sm max-w-2xl mx-auto">Curated packages combining favourite services for exceptional value.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {offers.map((offer) => {
                const savings = offer.total_price - offer.offer_price;
                const pct = Math.round((savings / offer.total_price) * 100);
                return (
                  <div key={offer.id} className="relative bg-[#0a2418] border border-[#c5a059]/30 p-6 rounded-sm hover:shadow-2xl hover:border-[#c5a059] transition-all">
                    <div className="absolute top-3 right-3 bg-[#c5a059] text-[#04140e] text-xs font-bold px-3 py-1 rounded-full">-{pct}%</div>
                    <h3 className="text-xl font-serif font-semibold text-[#f5e9ce] mb-3 pr-16">{localize(offer, 'title')}</h3>
                    {localize(offer, 'description') && (
                      <p className="text-xs text-[#f5e9ce]/70 mb-3 leading-relaxed line-clamp-3">{localize(offer, 'description')}</p>
                    )}
                    <div className="flex items-baseline space-x-3 mb-4">
                      <span className="text-white/40 line-through text-sm">€{offer.total_price.toFixed(0)}</span>
                      <span className="text-[#c5a059] font-serif font-bold text-3xl">€{offer.offer_price.toFixed(0)}</span>
                    </div>
                    <p className="text-white/50 text-xs mb-4">{offer.duration} min • Save €{savings.toFixed(0)}</p>
                    <Link to={`/book?promo=${offer.id}`}>
                      <Button className="w-full bg-[#c5a059] text-[#04140e] hover:bg-[#d4b56e] rounded-full font-semibold">Book Now <ArrowRight className="w-4 h-4 ml-2" /></Button>
                    </Link>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Testimonials */}
      <section className="py-16 bg-[#faf6ec] border-t border-[#c5a059]/15" data-testid="home-testimonials">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-10">
            <span className="text-[#c5a059] text-[11px] tracking-[0.3em]">— KIND WORDS</span>
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-[#0d3b25] mt-3 mb-2">What Our Clients Say</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((r, i) => (
              <div key={i} className="bg-white border border-[#c5a059]/30 rounded-sm p-7 flex flex-col relative">
                <Quote className="w-8 h-8 text-[#c5a059]/25 absolute top-5 right-5" />
                <div className="flex gap-0.5 mb-4">
                  {Array.from({ length: r.rating }).map((_, j) => (
                    <Star key={j} className="w-4 h-4 fill-[#c5a059] text-[#c5a059]" />
                  ))}
                </div>
                <p className="text-[#0d3b25]/80 text-sm leading-relaxed mb-5 flex-grow">"{r.text}"</p>
                <p className="font-serif font-semibold text-[#0d3b25]">{r.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact / Map */}
      <section className="py-16 bg-[#04140e] border-t border-[#c5a059]/15" data-testid="home-contact">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-10 items-stretch">
          <div className="flex flex-col justify-center">
            <span className="text-[#c5a059] text-[11px] tracking-[0.3em]">— VISIT US</span>
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-[#f5e9ce] mt-3 mb-6">Find Your Way to 4AN</h2>
            <div className="space-y-5">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-full border border-[#c5a059] flex items-center justify-center text-[#c5a059] flex-shrink-0"><MapPin className="w-5 h-5" /></div>
                <div>
                  <p className="text-[10px] tracking-widest text-[#c5a059] mb-1">ADDRESS</p>
                  <p className="text-sm text-[#f5e9ce]/90">Alfons de Cockplein 5A<br/>1831 Diegem, Belgium</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-full border border-[#c5a059] flex items-center justify-center text-[#c5a059] flex-shrink-0"><Phone className="w-5 h-5" /></div>
                <div>
                  <p className="text-[10px] tracking-widest text-[#c5a059] mb-1">PHONE / WHATSAPP</p>
                  <a href="tel:+32473183927" className="text-sm text-[#f5e9ce]/90 hover:text-[#c5a059] transition-colors">+32 473 18 39 27</a>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-full border border-[#c5a059] flex items-center justify-center text-[#c5a059] flex-shrink-0"><Clock className="w-5 h-5" /></div>
                <div>
                  <p className="text-[10px] tracking-widest text-[#c5a059] mb-1">OPENING HOURS</p>
                  <p className="text-sm text-[#f5e9ce]/90">10:00 – 19:00 · Monday Closed</p>
                </div>
              </div>
            </div>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/contact">
                <Button className="bg-[#c5a059] text-[#04140e] hover:bg-[#d4b56e] rounded-full font-semibold px-7">Contact Us</Button>
              </Link>
              <a href="https://www.google.com/maps/dir/?api=1&destination=Alfons+de+Cockplein+5A,+1831+Diegem,+Belgium" target="_blank" rel="noopener noreferrer">
                <Button variant="outline" className="border-[#c5a059] text-[#c5a059] hover:bg-[#c5a059] hover:text-[#04140e] rounded-full bg-transparent px-7">Get Directions</Button>
              </a>
            </div>
          </div>
          <div className="rounded-sm overflow-hidden border border-[#c5a059]/40 shadow-2xl min-h-[320px]">
            <iframe
              title="4AN Hair & Beauty Studio — Alfons de Cockplein 5A, 1831 Diegem"
              src="https://www.google.com/maps?q=Alfonse+de+Cockplein+5A,+1831+Diegem,+Belgium&output=embed"
              width="100%"
              height="100%"
              style={{ border: 0, minHeight: 320 }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              data-testid="home-map-iframe"
              allowFullScreen
            />
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-[#faf6ec] border-t border-[#c5a059]/15 text-center">
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-serif font-bold mb-4 text-[#0d3b25]">Ready for a Stunning Transformation?</h2>
          <p className="text-[#0d3b25]/70 mb-8">Book your appointment today and let us bring out the best in you.</p>
          <Link to="/services">
            <Button size="lg" className="bg-[#c5a059] text-[#04140e] hover:bg-[#d4b56e] font-semibold px-12 py-6 rounded-full">
              <Calendar className="w-5 h-5 mr-2" /> Book Your Appointment
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
